import { isAbortError } from "@/shared/errors";

/**
 * Tracks whether a filter application is in flight for the panel's
 * "applying" indicator, with latest-wins semantics: only the newest tracked
 * promise may turn the indicator off.
 *
 * An abort rejection deliberately does NOT turn the indicator off. Aborts
 * come from the filter bar's effect cleanup, which fires synchronously
 * before a debounced replacement apply is issued — while the worker may
 * still be crunching the aborted scan. Clearing on abort would report idle
 * for the whole debounce gap on a pegged worker. The one abort with no
 * replacement is the bar hiding, which must call {@link reset}.
 */
export interface FilterApplyTracker {
  /** Mark `application` as the newest in-flight apply. */
  track(application: Promise<unknown>): void;
  /** Force the indicator off and orphan any in-flight settles. */
  reset(): void;
}

export function createFilterApplyTracker(
  onApplyingChange: (isApplying: boolean) => void,
): FilterApplyTracker {
  let newestSeq = 0;

  return {
    track(application: Promise<unknown>): void {
      newestSeq += 1;
      const seq = newestSeq;
      onApplyingChange(true);
      application.then(
        () => {
          if (newestSeq === seq) {
            onApplyingChange(false);
          }
        },
        (err: unknown) => {
          if (isAbortError(err)) {
            return;
          }
          if (newestSeq === seq) {
            onApplyingChange(false);
          }
        },
      );
    },
    reset(): void {
      newestSeq += 1;
      onApplyingChange(false);
    },
  };
}
